import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Heart, Clock, ChefHat, ShoppingCart, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CocktailDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [cocktail, setCocktail] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cocktailRes, favoritesRes] = await Promise.all([
          axios.get(`${API}/cocktails/${id}`),
          axios.get(`${API}/favorites`, { headers: getAuthHeaders() })
        ]);
        
        setCocktail(cocktailRes.data);
        setIsFavorite(favoritesRes.data.some(f => f.cocktail_id === id));
      } catch (error) {
        console.error('Error fetching cocktail:', error);
        toast.error('Cocktail not found');
        navigate('/cocktails');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, getAuthHeaders, navigate]);

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`, { headers: getAuthHeaders() });
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { cocktail_id: id }, { headers: getAuthHeaders() });
        setIsFavorite(true);
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const addIngredientToList = async (ingredient) => {
    try {
      await axios.post(`${API}/shopping-list`, { item_name: ingredient }, { headers: getAuthHeaders() });
      toast.success(`Added ${ingredient} to shopping list`);
    } catch (error) {
      toast.error('Failed to add to shopping list');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="h-[400px] shimmer rounded-2xl mb-8" />
        <div className="h-8 w-48 shimmer rounded mb-4" />
        <div className="h-4 w-full shimmer rounded mb-2" />
        <div className="h-4 w-3/4 shimmer rounded" />
      </div>
    );
  }

  if (!cocktail) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="cocktail-detail">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-6 text-[#A3A3A3] hover:text-[#F5F5F5] -ml-2"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back
      </Button>

      {/* Hero Image */}
      <div className="relative rounded-2xl overflow-hidden mb-8 animate-fadeIn">
        <img
          src={cocktail.image_url}
          alt={cocktail.name}
          className="w-full h-[300px] md:h-[400px] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        
        <div className="absolute bottom-6 left-6 right-6">
          <span className="category-badge mb-3">{cocktail.category}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
            {cocktail.name}
          </h1>
          <div className="flex items-center gap-4 text-white/70 text-sm">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              5 min
            </span>
            <span className="flex items-center gap-1">
              <ChefHat className="w-4 h-4" />
              {cocktail.difficulty}
            </span>
          </div>
        </div>

        <button
          onClick={toggleFavorite}
          className={`absolute top-4 right-4 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
            isFavorite
              ? 'bg-[#EF4444] text-white'
              : 'bg-black/40 backdrop-blur text-white/70 hover:text-white'
          }`}
          data-testid="favorite-toggle-btn"
        >
          <Heart className={`w-6 h-6 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Ingredients */}
        <div className="md:col-span-1 animate-fadeIn stagger-1">
          <h2 className="text-2xl font-bold text-[#F5F5F5] mb-4">Ingredients</h2>
          <div className="glass rounded-2xl p-6">
            <ul className="space-y-3">
              {cocktail.ingredients.map((ingredient, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between group"
                >
                  <span className="text-[#F5F5F5] capitalize">{ingredient}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => addIngredientToList(ingredient)}
                    className="opacity-0 group-hover:opacity-100 text-[#A3A3A3] hover:text-[#D4AF37] transition-opacity"
                    data-testid={`add-ingredient-btn-${index}`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Instructions */}
        <div className="md:col-span-2 animate-fadeIn stagger-2">
          <h2 className="text-2xl font-bold text-[#F5F5F5] mb-4">Instructions</h2>
          <div className="bg-[#121212] border border-white/5 rounded-2xl p-6">
            <p className="text-[#A3A3A3] leading-relaxed text-lg">
              {cocktail.instructions}
            </p>
          </div>

          {/* Pro Tips */}
          <div className="mt-6 glass rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#D4AF37] mb-3 flex items-center gap-2">
              <Check className="w-5 h-5" />
              Pro Tips
            </h3>
            <ul className="space-y-2 text-[#A3A3A3] text-sm">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2" />
                Always use fresh ingredients for the best flavor
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2" />
                Chill your glass before serving for an extra cold drink
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2" />
                Measure ingredients precisely for consistent results
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4 animate-fadeIn stagger-3">
        <Button
          onClick={toggleFavorite}
          className={`flex-1 font-bold rounded-full py-6 btn-press ${
            isFavorite
              ? 'bg-[#EF4444] text-white hover:bg-[#DC2626]'
              : 'bg-[#D4AF37] text-black hover:bg-[#F4CF57] gold-glow'
          }`}
        >
          <Heart className={`w-5 h-5 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
          {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
        </Button>
        <Button
          onClick={() => navigate('/cocktails')}
          variant="outline"
          className="flex-1 border-white/20 text-[#A3A3A3] hover:text-[#F5F5F5] rounded-full py-6 btn-press"
        >
          Browse More Cocktails
        </Button>
      </div>
    </div>
  );
}
